import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useProductShippingMetadataDetail } from "../../../hooks/useProductShippingMetadata";

interface ProductShippingMetadataViewModalProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  retailer: string;
}

const ProductShippingMetadataViewModal: React.FC<ProductShippingMetadataViewModalProps> = ({
  open,
  onClose,
  productId,
  retailer,
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useProductShippingMetadataDetail(productId, retailer, open);

  const metadata = data?.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("admin.shippingMetadata.metadataDetails")}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Typography>{t("common.loading")}</Typography>
        ) : metadata ? (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.productCache.productId")}
                </Typography>
                <Typography variant="body1">{metadata.productId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.productCache.retailer")}
                </Typography>
                <Typography variant="body1">{metadata.retailer}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.productTitle")}
                </Typography>
                <Typography variant="body1">{metadata.productTitle || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.price")}
                </Typography>
                <Typography variant="body1">
                  {metadata.price ? `$${metadata.price.toFixed(2)}` : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.fixedShippingPrice")}
                </Typography>
                <Typography variant="body1">
                  {metadata.fixedShippingPrice ? `$${metadata.fixedShippingPrice.toFixed(2)}` : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.weightKg")}
                </Typography>
                <Typography variant="body1">
                  {metadata.weightInKg?.toFixed(3) || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.volumeCubicInches")}
                </Typography>
                <Typography variant="body1">
                  {metadata.volumeInCubicInches?.toFixed(2) || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.source")}
                </Typography>
                <Typography variant="body1">{metadata.source}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.createdAt")}
                </Typography>
                <Typography variant="body1">
                  {new Date(metadata.createdAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.shippingMetadata.updatedAt")}
                </Typography>
                <Typography variant="body1">
                  {new Date(metadata.updatedAt).toLocaleString()}
                </Typography>
              </Grid>
              {metadata.packageDimensionsJson && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("admin.shippingMetadata.packageDimensionsJson")}
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", maxHeight: 200, overflow: "auto" }}>
                    <pre style={{ margin: 0, fontSize: "12px" }}>
                      {JSON.stringify(JSON.parse(metadata.packageDimensionsJson), null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <Typography>{t("admin.shippingMetadata.metadataNotFound")}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductShippingMetadataViewModal;

